import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Tag, Percent, DollarSign, Calendar } from 'lucide-react';

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [form, setForm] = useState({
    id: null,
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_purchase: 0,
    max_discount: null,
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: '',
    is_active: true,
    usage_limit: null,
    usage_count: 0
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await axios.get('/admin/coupons', {
        withCredentials: true
      });
      setCoupons(response.data);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert dates to ISO format with time
      const submitData = {
        ...form,
        code: form.code.toUpperCase(),
        valid_from: new Date(form.valid_from).toISOString(),
        valid_until: new Date(form.valid_until + 'T23:59:59').toISOString(),
        discount_value: parseFloat(form.discount_value),
        min_purchase: parseFloat(form.min_purchase) || 0,
        max_discount: form.max_discount ? parseFloat(form.max_discount) : null,
        usage_limit: form.usage_limit ? parseInt(form.usage_limit) : null
      };

      if (form.id) {
        await axios.put(`/admin/coupons/${form.id}`, submitData, {
          withCredentials: true
        });
        toast.success('Coupon updated');
      } else {
        await axios.post('/admin/coupons', submitData, {
          withCredentials: true
        });
        toast.success('Coupon created');
      }

      setShowDialog(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      console.error('Failed to save coupon:', error);
      toast.error(error.response?.data?.detail || 'Failed to save coupon');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;

    try {
      await axios.delete(`/admin/coupons/${id}`, {
        withCredentials: true
      });
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete coupon');
    }
  };

  const resetForm = () => {
    setForm({
      id: null,
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_purchase: 0,
      max_discount: null,
      valid_from: new Date().toISOString().split('T')[0],
      valid_until: '',
      is_active: true,
      usage_limit: null,
      usage_count: 0
    });
  };

  const openEditDialog = (coupon) => {
    setForm({
      ...coupon,
      valid_from: new Date(coupon.valid_from).toISOString().split('T')[0],
      valid_until: new Date(coupon.valid_until).toISOString().split('T')[0]
    });
    setShowDialog(true);
  };

  const isExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };

  const getUsagePercentage = (used, limit) => {
    if (!limit) return 0;
    return (used / limit) * 100;
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Coupon Management</h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">Create and manage discount coupons</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" /> Create Coupon
        </Button>
      </div>

      {/* Coupons Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {coupons.map((coupon) => {
          const expired = isExpired(coupon.valid_until);
          const usagePercent = getUsagePercentage(coupon.usage_count, coupon.usage_limit);
          
          return (
            <Card key={coupon.id} className={`relative ${expired ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <Tag className="w-5 h-5 text-blue-600" />
                    <CardTitle className="text-lg font-mono">{coupon.code}</CardTitle>
                  </div>
                  {!coupon.is_active && (
                    <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">Inactive</span>
                  )}
                  {expired && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Expired</span>
                  )}
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-3">
                  {/* Discount */}
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      {coupon.discount_type === 'percentage' ? (
                        <Percent className="w-4 h-4 text-blue-600" />
                      ) : (
                        <DollarSign className="w-4 h-4 text-green-600" />
                      )}
                      <span className="text-sm text-gray-600">
                        {coupon.discount_type === 'percentage' ? 'Discount' : 'Fixed Off'}
                      </span>
                    </div>
                    <span className="text-xl font-bold text-blue-600">
                      {coupon.discount_type === 'percentage' 
                        ? `${coupon.discount_value}%` 
                        : `₹${coupon.discount_value}`}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="text-xs space-y-2">
                    {coupon.min_purchase > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min Purchase:</span>
                        <span className="font-semibold">₹{coupon.min_purchase}</span>
                      </div>
                    )}
                    
                    {coupon.max_discount && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max Discount:</span>
                        <span className="font-semibold">₹{coupon.max_discount}</span>
                      </div>
                    )}

                    <div className="flex justify-between">
                      <span className="text-gray-600">Valid Until:</span>
                      <span className="font-semibold">
                        {new Date(coupon.valid_until).toLocaleDateString()}
                      </span>
                    </div>

                    {coupon.usage_limit && (
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-600">Usage:</span>
                          <span className="font-semibold">
                            {coupon.usage_count}/{coupon.usage_limit}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${usagePercent}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button size="sm" variant="outline" onClick={() => openEditDialog(coupon)} className="flex-1">
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(coupon.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {coupons.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center text-gray-500">
              <Tag className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No coupons yet. Create your first coupon!</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit Coupon' : 'Create Coupon'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Coupon Code</label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="SAVE20"
                required
                maxLength={20}
              />
              <p className="text-xs text-gray-500 mt-1">
                Will be auto-converted to uppercase
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">Discount Type</label>
              <select
                value={form.discount_type}
                onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount (₹)</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium">
                Discount Value {form.discount_type === 'percentage' ? '(%)' : '(₹)'}
              </label>
              <Input
                type="number"
                value={form.discount_value}
                onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                placeholder={form.discount_type === 'percentage' ? '20' : '100'}
                min="0"
                step="0.01"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium">Minimum Purchase (₹)</label>
              <Input
                type="number"
                value={form.min_purchase}
                onChange={(e) => setForm({ ...form, min_purchase: e.target.value })}
                placeholder="0"
                min="0"
              />
            </div>

            {form.discount_type === 'percentage' && (
              <div>
                <label className="text-sm font-medium">Max Discount Cap (₹)</label>
                <Input
                  type="number"
                  value={form.max_discount || ''}
                  onChange={(e) => setForm({ ...form, max_discount: e.target.value || null })}
                  placeholder="Optional"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty for no cap
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Valid From</label>
                <Input
                  type="date"
                  value={form.valid_from}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Valid Until</label>
                <Input
                  type="date"
                  value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  min={form.valid_from}
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Usage Limit</label>
              <Input
                type="number"
                value={form.usage_limit || ''}
                onChange={(e) => setForm({ ...form, usage_limit: e.target.value || null })}
                placeholder="Unlimited"
                min="1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty for unlimited uses
              </p>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Active</label>
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowDialog(false); resetForm(); }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Coupon'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CouponManagement;
